import { useQuery } from '@apollo/client/react';
import Popover, { positionBounded } from '@components/Popover/Popover';
import Tooltip from '@components/Tooltip/Tooltip';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import useNode from '@hooks/use-node';
import useWorkspaceID from '@hooks/use-workspace-id';
import useWorkspaceUrl from '@hooks/use-workspace-url';
import { analytics } from '@modules/analytics';
import { useGlobalModal } from '@modules/global-modals/use-global-modal';
import { createPersonalCard } from '@modules/identity/createPersonalCard';
import { Box } from '@ui-lib';
import Router, { useRouter } from 'next/router';
import { useState } from 'react';

import { SelectorBarAddNewButton } from '../../../../../components/SelectorBarAddNewButton';
import { ActorCardsAndProfilesDocument } from '../../actor-cards-profiles.graphql';
import { MaxPersonalCardsDialog } from '../MaxPersonalCardsDialog/MaxPersonalCardsDialog';
import * as styles from './SelectorBar.css';

export const SelectorBarAddNewPersonalCardButton: React.FC<
  React.PropsWithChildren<{ id: string }>
> = ({ id }) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data, loading } = useQuery(ActorCardsAndProfilesDocument, {
    variables: {
      timezone,
    },
    fetchPolicy: 'cache-and-network',
  });
  const { openModal } = useGlobalModal({
    registeredModals: ['UPGRADE'],
  });

  const workspaceId = useWorkspaceID();
  const { planType, maxPersonalCards } = useActorPlan();
  const router = useRouter();

  const teamWorkspaceRole = data?.actor?.profiles?.find(
    (profile) => profile.workspace?.id === workspaceId && profile.workspace?.type === 'TEAM',
  )?.role;

  const isTeamAdmin = teamWorkspaceRole === 'ADMIN' || teamWorkspaceRole === 'OWNER';
  const isTeamMember = teamWorkspaceRole === 'MEMBER';

  // number of personal cards user has from all profiles
  const cardNumber = data?.actor?.cards?.edges?.length;

  const newTeamCardUrl = useWorkspaceUrl('cards/team/new');
  const [targetNode, targetRef] = useNode<HTMLDivElement>();
  const [showCardSelectionPopover, setShowCardSelectionPopover] = useState(false);
  const [showMaxPersonalCardsDialog, setShowMaxPersonalCardsDialog] = useState(false);

  // biome-ignore lint/correctness/noNestedComponentDefinitions: historic
  const AddBtnClick = () => {
    analytics.track({
      name: 'selector_bar_button_clicked',
      platform: 'dash',
      properties: {
        workspace_role: teamWorkspaceRole,
      },
    });

    // If the user is already team member of a workspace, direct them to create a personal card
    if (isTeamMember && !isTeamAdmin) {
      handleCreateCard('personal');
    }
    // if user has free plan and reached maximum number of cards, show them the upgrade dialog
    if (planType === 'free' && cardNumber && cardNumber >= maxPersonalCards) {
      openModal({
        type: 'UPGRADE',
        trackingSource: 'side-nav',
      });
      return;
    }

    // if user has premium or team plan, and reached maximum number of cards, show them the max cards dialog
    if (
      (planType === 'premium' || planType === 'team') &&
      cardNumber &&
      cardNumber >= maxPersonalCards
    ) {
      setShowMaxPersonalCardsDialog(true);
      return;
    }

    // if user has less than 5 cards, show popover so they can decide to create personal card or team card for themselves
    // even if user isn't a team admin, this popover should still show up so they can see this feature is available if they upgrade
    setShowCardSelectionPopover((prevBool) => !prevBool);
  };

  const goToNewPersonalCard = async () => {
    const currentCardId = typeof router.query.cardId === 'string' ? router.query.cardId : undefined;
    // The route resolves the UP rollout flags server-side and returns either the
    // new identity editor URL (creating a draft) or the legacy /cards/personal/new.
    const url = await createPersonalCard({ sourceCardId: currentCardId });
    await Router.push(url);
  };

  const handleCreateCard = (cardType: 'personal' | 'team') => {
    if (cardType === 'personal') {
      analytics.track({
        name: 'selector_bar_me_option_clicked',
        platform: 'dash',
        properties: {
          workspace_role: teamWorkspaceRole,
        },
      });

      void goToNewPersonalCard();
      return;
    }

    if (cardType === 'team') {
      analytics.track({
        name: 'selector_bar_team_member_option_clicked',
        platform: 'dash',
        properties: {
          workspace_role: teamWorkspaceRole,
        },
      });

      Router.push(newTeamCardUrl);
      return;
    }
  };

  const clickTeamMember = () => {
    analytics.track({ name: 'teamcreatecardbutton_clicked' });
    handleCreateCard('team');
  };

  const tooltipText =
    planType === 'free'
      ? 'Upgrade to a Blinq Premium or Blinq Business account to create more cards'
      : 'You have reached the maximum number of personal cards';

  if (loading) {
    return null;
  }

  return (
    <>
      <Popover
        positioner={positionBounded({ offsetY: 8 })}
        targetNode={targetNode}
        show={showCardSelectionPopover}
        onClose={() => setShowCardSelectionPopover(false)}
        preventOutsideInteraction
        drawFocus
      >
        <Box className={styles.cardTypeSelectionPopover}>
          <h3 className={styles.cardTypeSelectionPopoverHeader}>Who is this card for?</h3>
          <Box className={styles.cardTypeSelectionPopoverBtns}>
            <button
              className={styles.cardTypeSelectionPopoverBtn}
              onClick={() => handleCreateCard('personal')}
            >
              Me
            </button>
            {isTeamAdmin && (
              <button className={styles.cardTypeSelectionPopoverBtn} onClick={clickTeamMember}>
                <span>A team member</span>
              </button>
            )}
          </Box>
        </Box>
      </Popover>

      <MaxPersonalCardsDialog
        show={showMaxPersonalCardsDialog}
        close={() => setShowMaxPersonalCardsDialog(false)}
        planName={planType}
        maxCardNumber={maxPersonalCards}
        handleUpgradeClick={() =>
          openModal({
            type: 'UPGRADE',
            trackingSource: 'side-nav',
          })
        }
      />

      <Box ref={targetRef}>
        {cardNumber && cardNumber >= maxPersonalCards ? (
          <Tooltip size="small" label={tooltipText} aria-label="Feature lock message">
            <div>
              <SelectorBarAddNewButton id={id} onClick={AddBtnClick} maxCardsReached={true} />
            </div>
          </Tooltip>
        ) : (
          <SelectorBarAddNewButton id={id} onClick={AddBtnClick} />
        )}
      </Box>
    </>
  );
};
